/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   10/08/2022
 */

import React from "react";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Stack
} from "@mui/material";
import { useMutation, useQuery } from "urql";
import {
	SummaryNotificationsQuery,
	SummaryNotificationsQueryVars,
	SummaryNotificationsResponse
} from "../../graphql/queries/summary-notifications";
import { useSelector } from "react-redux";
import { State } from "../../state/store";
import HeroMessage from "../../components/HeroMessage";
import LoadingHero from "../../components/LoadingHero";
import NoSummaries from "./NoSummaries";
import { Link, useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { dateTimeCol, format } from "../../dates-helper";
import { SummaryNotification, SummaryNotificationConfig } from "../../interfaces/Global";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { LoadingButton } from "@mui/lab";
import {
	DeleteSummaryNotificationMutation,
	DeleteSummaryNotificationResponse,
	DeleteSummaryNotificationVars,
	UpdateSummaryNotificationMutation,
	UpdateSummaryNotificationResponse,
	UpdateSummaryNotificationVars
} from "../../graphql/mutations/summary-notifications";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";

export default function SummariesList() {
	
	const navigate = useNavigate();

	const user = useSelector((s:State) => s.user);

	const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = React.useState(false);
	const [deleteItemId, setDeleteItemId] = React.useState(0);

	const [ summariesResult ] = useQuery<SummaryNotificationsResponse, SummaryNotificationsQueryVars>({
		query: SummaryNotificationsQuery,
		requestPolicy: "cache-and-network",
		variables: {
			createdBy: user?.id,
			orderBy: "id"
		}
	});

	const [, updateSummaryNotification] = useMutation<UpdateSummaryNotificationResponse, UpdateSummaryNotificationVars>(UpdateSummaryNotificationMutation);
	const [deleteResult, deleteSummaryNotification] = useMutation<DeleteSummaryNotificationResponse, DeleteSummaryNotificationVars>(DeleteSummaryNotificationMutation);


	const summaries = summariesResult.data?.summaryNotifications.items || [];

	return (
		<Stack>
			{summariesResult.error &&
				<HeroMessage
					p={4}
					title={"Request Error"}
					titleTypographyProps={{
						variant: "h6"
					}}
					message={summariesResult.error.message}
				/>
			}
			{summariesResult.fetching &&
				<LoadingHero />
			}
			{!summariesResult.error && !summariesResult.fetching && summaries.length === 0 &&
				<NoSummaries />
			}
			{!summariesResult.error && !summariesResult.fetching && summaries.length > 0 &&
				<>
					<Box sx={{ mb: 1 }}>
						<Button
							variant={"outlined"}
							color={"primary"}
							component={Link}
							to={"/notification/summaries/new"}
							startIcon={<AddIcon />}
						>
							New Summary Notification
						</Button>
					</Box>
					<DataGrid<Partial<SummaryNotification>>
						rows={summaries}
						autoHeight
						hideFooterSelectedRowCount
						loading={summariesResult.fetching}
						density={"compact"}
						disableColumnMenu
						columns={[
							{ field: "id", headerName: "ID", width: 70 },
							{ field: "creationDate", ...dateTimeCol, headerName: "Creation Date" },
							{
								field: "channel",
								valueGetter: (params) => {
									return params.row?.config?.channel?.name ?? null;
								},
								valueFormatter: (params) => {
									if (!params.value) return "Myself";
									const channel = summaries.find(i => i.config?.channel?.name === params.value)?.config?.channel;
									if (!channel) return null;
									return (channel.isPrivate ? "🔒" : "#") + params.value;
								},
								headerName: "Sent To",
								flex: 1
							}, {
								field: "daysOfWeek",
								valueGetter: (params) => {
									return params.row?.config?.daysOfWeek;
								},
								valueFormatter: (params) => {
									return (params.value as string[])?.join(", ");
								},
								headerName: "Days Of Week",
								flex: 1
							}, {
								field: "times",
								valueGetter: (params) => {
									return params.row?.config?.times;
								},
								valueFormatter: (params) => {
									return (params.value as string[])?.map(t => {
										const [h,m] = t.split(":");
										const date = new Date();
										date.setHours(Number(h));
										date.setMinutes(Number(m));
										return format(date, 'p');
									}).join(", ");
								},
								headerName: "Times Of Day",
								flex: 1
							}, {
								field: "employees",
								valueGetter: (params) => {
									return params.row?.config?.employeesSource;
								},
								valueFormatter: (params) => {
									return params.value === "calendar" ? "From linked calendar" : "Custom filter";
								},
								headerName: "Employees",
								flex: 1
							}, {
								field: "content",
								valueGetter: (params) => {
									return params.row?.config;
								},
								valueFormatter: (params) => {
									const config = params.value as SummaryNotificationConfig;
									const str: string[] = [];
									if (config.includeUnavailable) str.push("Unavailable");
									if (config.includeAvailable) str.push("Available");
									if (config.includeEventCategories) str.push("Event Categories: " + (config.eventCategories.length === 0 ? "all" : config.eventCategories.join(",")));
									if (config.includeEventTypes) str.push("Event Types: " + (config.eventTypes.length === 0 ? "all" : config.eventTypes.join(",")));
									return str.join(", ");
								},
								headerName: "Content",
								flex: 1
							}, {
								field: "enabled",
								headerName: "Enabled",
								headerAlign: "center",
								align: "center",
								valueFormatter: (params) => {
									return params.value ? "Yes" : "No";
								},
							}, {
								field: "actions",
								type: "actions",
								width: 50,
								getActions: (params) => [
									<GridActionsCellItem
										icon={<EditIcon fontSize={"small"} />}
										label="Edit"
										onClick={() => {
											navigate("/notification/summaries/" + params.row.id);
										}}
										showInMenu
									/>,
									<GridActionsCellItem
										icon={<PowerSettingsNewIcon fontSize={"small"} />}
										label={params.row.enabled ? "Disable" : "Enable"}
										onClick={() => {
											updateSummaryNotification({ id: Number(params.row.id), data: { enabled: !params.row.enabled }}).then();
										}}
										showInMenu
									/>,
									<GridActionsCellItem
										icon={<DeleteIcon fontSize={"small"} />}
										label="Delete"
										onClick={() => {
											setDeleteItemId(Number(params.row.id));
											setConfirmDeleteDialogOpen(true);
										}}
										showInMenu
									/>,
								],
							}
						]}
					/>
					<Dialog
						open={confirmDeleteDialogOpen}
						onClose={() => { setConfirmDeleteDialogOpen(false)}}
					>
						<DialogTitle>
							Confirm delete
						</DialogTitle>
						<DialogContent>
							<DialogContentText>
								Are you sure you want to delete this notification? This operation is irreversible.
							</DialogContentText>
						</DialogContent>
						<DialogActions>
							<Button onClick={() => setConfirmDeleteDialogOpen(false)}>Cancel</Button>
							<LoadingButton
								loading={deleteResult.fetching}
								onClick={() => {
									setConfirmDeleteDialogOpen(false);
									deleteSummaryNotification({id: deleteItemId}).then(() => setDeleteItemId(0));
								}}
								color={"primary"}
								variant={"contained"}
							>
								Delete
							</LoadingButton>
						</DialogActions>
					</Dialog>
				</>
			}
		</Stack>
	);

}